import { siteApiSlice } from "../../app/siteApiSlice";
import { Accessibility, UserRole } from "../../app/userSlice";
import { DataTablesQueryModel, DataTablesViewModel } from "../../interfaces/dataTables";
import NamedEntity from "../../interfaces/NamedEntity";
import { axStringToAccessibility } from "../accessibility/accessibilityUtil";
import { RulesModel } from "../account/accountApiSlice";
import { CourseStatus } from "../courses/coursesApiSlice";

export const Gender = {
	Female: "Female",
	Male: "Male",
};

export const Ethnicity = {
	AfricanAmerican: "African-American",
	AsianPacificIslander: "Asian and Pacific Islander",
	Hispanic: "Hispanic",
	NativeAmerican: "Native American",
	White: "White",
};

export const roleColors: Record<UserRole, string> = {
	[UserRole.Admin]: "bg-blue-300",
	[UserRole.StateAdmin]: "bg-amber-300",
	[UserRole.DistrictAdmin]: "bg-emerald-300",
	[UserRole.SchoolAdmin]: "bg-pink-300",
	[UserRole.Proctor]: "bg-orange-300",
	[UserRole.Writer]: "bg-purple-300",
	[UserRole.Learner]: "bg-slate-300",
};

interface UserSelfBaseModel {
	userId: number;
	role: UserRole;
	isTeachersWriterAccount: boolean;
	username: string;
	firstName: string;
	lastName: string;
	initials: string;
	userImageUploadDisabled: boolean;
	hasImage: boolean;
	grade: string;
	requiresAccountConfirmation: boolean;
	bypassTermsOfService: boolean;
	isImpersonating: boolean;
	school: NamedEntity;
	district: NamedEntity;
	cleverId: string;
}

export interface ProctorUserModel {
	rules: RulesModel;
	id: number;
	userName: string;
	fName: string;
	lName: string;
	initials: string;
	dateOfBirth: string;
	externalID: string;
	gradeId: number;
	scoringLevelId: number;
	password: string;
	isCleverUser: boolean;
	isClassLinkUser: boolean;
	allowUserImages: boolean;
	userImageDisabledBy: string;
	hasImage: boolean;
	isEditing: boolean;
	ethnicity?: string;
	gender?: string;
	economicallyDisadvantaged?: boolean;
	limitedEnglish?: boolean;
}

export interface ResetPasswordViewModel {
	userId: number;
	password: string;
	confirmPassword: string;
}

export interface UserSelfViewModel extends UserSelfBaseModel {
	accessibility: string | null;
}

export interface UserSelfModel extends UserSelfBaseModel {
	accessibility: Accessibility | null;
}

export interface SaveAccessibilityModel {
	accessibility: string;
}

export interface ManageUserListViewModel {
	creatableRoles: UserRole[];
	viewableRoles: UserRole[];
}

export interface UsersListQueryModel extends DataTablesQueryModel {
	roleFilter: string;
	nameFilter: string;
	showInactiveFilter: boolean;
	districtFilter: number;
	schoolFilter: number;
}

export interface UserListViewModel {
	userId: string;
	userName: string;
	fullName: string;
	externalId: string;
	school: string;
	role: string;
	roleDescription: string;
	isTeachersWriterAccount: boolean;
	accountStatus: string;
	isClassLinkUser: boolean;
	isCleverUser: boolean;
	loginAt: string;
	hasImage: boolean;
	initials: string;
	canBeDeleted: boolean;
	canBeImpersonated: boolean;
}

export interface CreateUserViewModel {
	rules: RulesModel;
	roleOfCurrentUser: UserRole;
	roleToCreate: UserRole;
	fName: string;
	lName: string;
	disabled: boolean;
	allowUserImages: boolean;
	dateOfBirth: string;
	externalID: string;
	emailAddress: string;
	isEmailAddressRequired: boolean;
	welcomeMailFeature: boolean;
	sendWelcomeMail: boolean;
	phone: string;
	generateUserNameAndPassword: boolean;
	useCredentialsGeneratorFeature: boolean;
	userName: string;
	password: string;
	confirmPassword: string;
	gradeId?: number;
	ethnicity?: string;
	gender?: string;
	economicallyDisadvantaged?: boolean;
	limitedEnglish?: boolean;
	disability?: number;
	district?: NamedEntity;
	school?: NamedEntity;
}

export interface EditUserViewModel {
	rules: RulesModel;
	id: number;
	role: UserRole;
	fullName: string;
	editorsRole: UserRole;
	lastLoginDate: Date;
	createDate: Date;
	testsTaken: number;
	currentPassword: string;
	districts: UserDistrictViewModel[];
	schools: UserSchoolViewModel[];
	courses: UserCourseViewModel[];
	anyDistrictsAvailable: boolean;
	anySchoolsAvailable: boolean;
	showSchoolForCourses: boolean;
	showDistrictForSchools: boolean;
	disabled: boolean;
	canRemoveUserImage: boolean;
	allowUserImages: boolean;
	userImageDisabledBy: string;
	hasImage: boolean;
	initials: string;
	isTeachersWriterAccount: boolean;
	unremovableDistrictId: number;
	cleverID: string;
	showCleverID: boolean;
	classLinkID: string;
	showClassLinkID: boolean;
	showWelcomeEmailResend: boolean;
	editModel: UserEditModel;
}

export interface UserEditModel {
	fName: string;
	lName: string;
	dateOfBirth: string;
	externalID: string;
	emailAddress: string;
	isEmailAddressRequired: boolean;
	phone: string;
	userName: string;
	gradeId?: number;
	ethnicity?: string;
	gender?: string;
	economicallyDisadvantaged?: boolean;
	limitedEnglish?: boolean;
	disability?: number;
}

export interface UserDistrictViewModel {
	districtID: number;
	name: string;
	isClassLinkDistrict: boolean;
	isCleverDistrict: boolean;
	city: string;
	state: string;
	code: string;
	externalID: string;
}

export interface UserSchoolViewModel {
	schoolID: number;
	name: string;
	active: boolean;
	isClassLinkSchool: boolean;
	isCleverSchool: boolean;
	externalID: string;
	address1: string;
	address2: string;
	city: string;
	contactName: string;
	phone: string;
	districtID: number;
	districtName: string;
	seatsRemaining: number;
	canEditSchool: boolean;
}

export interface UserCourseViewModel {
	courseID: number;
	name: string;
	isClassLinkCourse: boolean;
	isCleverCourse: boolean;
	externalID: string;
	createDate: Date;
	status: CourseStatus;
	schoolID: number;
	schoolName: string;
	grade: string;
	canEditCourse: boolean;
}

export interface ResendWelcomeEmailModel {
	userId: number;
	districtName: string;
	schoolName: string;
}

export interface UserOutsideSiteViewModel {
	userId: number;
	lastSyncAt: Date;
}

export interface UserCleverSettingsViewModel extends UserOutsideSiteViewModel {
	cleverId: string;
}

export interface UserClassLinkSettingsViewModel extends UserOutsideSiteViewModel {
	classLinkId: string;
}

export interface UserLogViewModel {
	indexId: number;
	actionTime: Date;
	actionType: string;
	course: string;
	ipAddress: string;
	os: string;
	device: string;
	browser: string;
}

export interface UserPrintViewModel {
	dT_RowId: string;
	userName: string;
	name: string;
	externalID: string;
	role: string;
}

export const usersApiSlice = siteApiSlice
	.enhanceEndpoints({
		addTagTypes: ["UserCleverSettings", "UserClassLinkSettings"],
	})
	.injectEndpoints({
		endpoints: (build) => ({
			getSelf: build.mutation<UserSelfModel, void>({
				query: () => ({
					url: `/User/GetSelf`,
					method: "GET",
				}),
				transformResponse: (response: UserSelfViewModel, meta, arg): UserSelfModel => {
					const transform: UserSelfModel = { ...response, accessibility: null };
					if (response.accessibility) {
						transform.accessibility = axStringToAccessibility(response.accessibility);
					}
					return transform;
				},
			}),
			saveAccessibility: build.mutation<string, string | null>({
				query: (payload) => ({
					url: `/User/SaveAccessibility`,
					method: "POST",
					body: { accessibility: payload },
					formData: true,
				}),
			}),
			manageUserList: build.query<ManageUserListViewModel, void>({
				query: () => ({
					url: `/User/List`,
				}),
			}),
			getUsersList: build.query<DataTablesViewModel<UserListViewModel>, UsersListQueryModel>({
				query: (q) => {
					const {
						iSortCol_0,
						sSortDir_0,
						iDisplayStart,
						iDisplayLength,
						roleFilter,
						nameFilter,
						districtFilter,
						schoolFilter,
						showInactiveFilter,
					} = q;
					return {
						url: `/User/UsersAjaxHandler`,
						params: {
							iSortCol_0,
							sSortDir_0,
							iDisplayStart,
							iDisplayLength,
							roleFilter,
							nameFilter,
							districtFilter,
							schoolFilter,
							showInactiveFilter,
						},
					};
				},
				providesTags: ["CurrentSchool", "CurrentDistrict", "UserList", "SeatCount"],
			}),
			getCreateUserInfo: build.query<CreateUserViewModel, string>({
				query: (role) => ({
					url: `/User/Create`,
					params: { role },
				}),
				providesTags: ["CurrentSchool", "CurrentDistrict"],
			}),
			createUser: build.mutation<
				{ userId: number; successMessage: string },
				CreateUserViewModel
			>({
				query: (payload) => {
					return {
						url: `/User/CreateUserOnSpa`,
						method: "POST",
						body: payload,
					};
				},
				invalidatesTags: ["UserList"],
			}),
			getUserInfo: build.query<EditUserViewModel, number>({
				query: (id) => ({
					url: `/User/Edit`,
					params: { id },
				}),
				providesTags: ["UserInfo", "SeatCount"],
			}),
			editUser: build.mutation<void, { id: number; editModel: UserEditModel }>({
				query: (payload) => {
					return {
						url: `/User/EditUserOnSpa`,
						method: "POST",
						body: payload,
					};
				},
				invalidatesTags: ["UserInfo", "UserList"],
			}),
			editStudentInfo: build.query<ProctorUserModel, number>({
				query: (id) => ({
					url: `/CourseWriters/EditStudent`,
					params: { id },
				}),
				providesTags: ["ManageCourse", "CurrentCourse"],
			}),
			editStudent: build.mutation<void, ProctorUserModel>({
				query: (payload) => {
					return {
						url: `/CourseWriters/EditStudent`,
						method: "POST",
						body: payload,
					};
				},
				invalidatesTags: ["ManageCourse"],
			}),
			resetUserPasswordByAdmin: build.mutation<void, ResetPasswordViewModel>({
				query: (payload) => {
					return {
						url: `/User/ResetUserPassword`,
						method: "POST",
						body: payload,
					};
				},
				invalidatesTags: ["UserInfo"],
			}),
			resetStudentPassword: build.mutation<void, ResetPasswordViewModel>({
				query: (payload) => {
					return {
						url: `/CourseWriters/ResetPassword`,
						method: "POST",
						body: payload,
					};
				},
				invalidatesTags: ["ManageCourse"],
			}),
			updateUserActiveStatus: build.mutation<void, { userId: number; disabled: boolean }>({
				query: (payload) => {
					return {
						url: `/User/UpdateUserActiveStatus`,
						method: "POST",
						body: payload,
					};
				},
				invalidatesTags: ["UserInfo", "UserList", "SeatCount"],
			}),
			updateUserAvatarSettingForUser: build.mutation<
				void,
				{ userId: number; allowImages: boolean }
			>({
				query: (payload) => {
					return {
						url: `/User/UpdateUserAvatarSettingForUser`,
						method: "POST",
						body: payload,
					};
				},
				invalidatesTags: ["UserInfo"],
			}),
			resendWelcomeEmail: build.mutation<string, ResendWelcomeEmailModel>({
				query: (payload) => {
					return {
						url: `/User/ResendWelcomeEmailAsync`,
						method: "POST",
						body: payload,
					};
				},
			}),
			getUserCleverSettings: build.query<UserCleverSettingsViewModel, number>({
				query: (userId) => ({
					url: `/User/EditCleverSettings`,
					method: "GET",
					params: { userId },
				}),
				providesTags: ["UserCleverSettings"],
			}),
			updateUserCleverSettings: build.mutation<void, UserCleverSettingsViewModel>({
				query: (payload) => ({
					url: `/User/EditCleverSettings`,
					method: "POST",
					body: payload,
				}),
				invalidatesTags: ["UserInfo", "UserList", "UserCleverSettings"],
			}),
			getUserClassLinkSettings: build.query<UserClassLinkSettingsViewModel, number>({
				query: (userId) => ({
					url: `/User/EditClassLinkSettings`,
					method: "GET",
					params: { userId },
				}),
				providesTags: ["UserClassLinkSettings"],
			}),
			updateUserClassLinkSettings: build.mutation<void, UserClassLinkSettingsViewModel>({
				query: (payload) => ({
					url: `/User/EditClassLinkSettings`,
					method: "POST",
					body: payload,
				}),
				invalidatesTags: ["UserInfo", "UserList", "UserClassLinkSettings"],
			}),
			userLog: build.query<UserLogViewModel[], number>({
				query: (id) => ({
					url: `/User/ViewLog`,
					params: { id },
				}),
				providesTags: ["UserInfo"],
			}),
			printUsers: build.query<UserPrintViewModel[], UsersListQueryModel>({
				query: (payload) => ({
					url: `/User/PrintUsersAjaxHandler`,
					method: "POST",
					body: payload,
				}),
			}),
		}),
	});

export const {
	useGetSelfMutation,
	useSaveAccessibilityMutation,
	useManageUserListQuery,
	useGetUsersListQuery,
	useGetCreateUserInfoQuery,
	useCreateUserMutation,
	useGetUserInfoQuery,
	useEditUserMutation,
	useEditStudentInfoQuery,
	useEditStudentMutation,
	useResetUserPasswordByAdminMutation,
	useResetStudentPasswordMutation,
	useUpdateUserActiveStatusMutation,
	useUpdateUserAvatarSettingForUserMutation,
	useResendWelcomeEmailMutation,
	useGetUserCleverSettingsQuery,
	useUpdateUserCleverSettingsMutation,
	useGetUserClassLinkSettingsQuery,
	useUpdateUserClassLinkSettingsMutation,
	useUserLogQuery,
	useLazyPrintUsersQuery,
} = usersApiSlice;
